import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import GoogleMap from '../components/GoogleMap.jsx';
import RegistroAtendimento from '../components/RegistroAtendimento.jsx';
import TecnicoVisitasModal from '../components/TecnicoVisitasModal.jsx';
import { osLabel, iconeTipoOs, diffMin, formatDuracaoMin } from '../lib/visita.js';
import { visitas, tecnicos as tecnicosApi } from '../lib/api.js';
import { formatHoraSC, formatDataHoraSC } from '../lib/tempo.js';
import { classificarVisita, COR_STATUS_VISUAL } from '../lib/statusVisual.js';

const TIPO_LABEL = {
  criada: 'Visita criada',
  tecnico_chegou: 'Técnico chegou',
  tecnico_saiu_cliente: 'Técnico saiu do cliente',
  tecnico_iniciou: 'Atendimento iniciado',
  tecnico_finalizou: 'Atendimento finalizado',
  cancelada: 'Cancelada',
  reagendada: 'Reagendada',
  observacao: 'Observação',
  tracking_token_emitido: 'Link de acompanhamento gerado',
};

// Status terminais — escondem ações de mutação (cancelar/reagendar/link)
const STATUS_TERMINAIS = new Set(['concluida', 'cancelada', 'nao_compareceu']);

// ──────────────────────────────────────────────────────────────────
// Mini-componentes internos

function GeocodeBadge({ qualidade }) {
  if (!qualidade) return <span style={badgeStyle('#6b7280', '#f3f4f6')}>📍 sem localização</span>;
  const m = {
    house:  { cor: '#15803d', bg: '#dcfce7', tx: '📍 endereço exato' },
    rua:    { cor: '#1e6fc8', bg: '#dbeafe', tx: '📍 na rua' },
    bairro: { cor: '#a16207', bg: '#fef3c7', tx: '📍 no bairro (aprox.)' },
  };
  const c = m[qualidade] || { cor: '#6b7280', bg: '#f3f4f6', tx: `📍 ${qualidade}` };
  return <span style={badgeStyle(c.cor, c.bg)}>{c.tx}</span>;
}

function StatusBadge({ statusVisual }) {
  const cor = COR_STATUS_VISUAL[statusVisual] || COR_STATUS_VISUAL.agendada;
  return (
    <span style={{
      padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
      background: cor.fundo, color: cor.texto, border: `1.5px solid ${cor.borda}`,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cor.label}
    </span>
  );
}

function badgeStyle(cor, bg) {
  return {
    fontSize: '0.7rem', fontWeight: 600,
    padding: '2px 8px', borderRadius: 10,
    color: cor, background: bg, border: `1px solid ${cor}40`,
    whiteSpace: 'nowrap',
  };
}

// Avatar do técnico — foto se houver, senão iniciais com cor do setor
function AvatarTecnico({ tecnico, size = 32 }) {
  const cor = tecnico?.avatar_cor || tecnico?.tecnico_avatar_cor || '#94a3b8';
  const iniciais = (tecnico?.apelido || tecnico?.tecnico_apelido || tecnico?.nome || '??')
    .split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  if (tecnico?.foto_url) {
    return <img src={tecnico.foto_url} alt={iniciais}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover',
               border: `2px solid ${cor}` }}/>;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: cor,
      color: 'white', fontWeight: 700, fontSize: size * 0.4,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, border: '2px solid white', boxShadow: `0 0 0 1.5px ${cor}`,
    }}>{iniciais}</div>
  );
}

// Métrica pequena horizontal (label + valor)
function Metrica({ label, valor, cor }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 16, color: cor || '#111827', fontWeight: 700, lineHeight: 1.1 }}>{valor}</div>
    </div>
  );
}

// Estado vazio elegante quando não tem coordenadas
function MapaVazio({ visita }) {
  return (
    <div style={{
      height: 380, background: '#f9fafb',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, color: '#6b7280',
    }}>
      <div style={{ fontSize: 48, opacity: 0.6 }}>🗺️</div>
      <div style={{ textAlign: 'center', padding: '0 1rem' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
          Endereço sem coordenadas geográficas
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          {visita?.cliente_endereco
            ? `"${visita.cliente_endereco}, ${visita.cliente_bairro}, ${visita.cliente_cidade}"`
            : 'Cliente sem endereço cadastrado'}
        </div>
        <div style={{ fontSize: 11, marginTop: 8, color: '#9ca3af' }}>
          A geocodificação roda automaticamente todo dia às 04h.
        </div>
      </div>
    </div>
  );
}

// Timeline com cor por tipo de evento
function TimelineEvento({ evento, anterior }) {
  // Cor por tipo
  let cor = '#94a3b8', icone = '●';
  if (evento.tipo === 'tecnico_iniciou')   { cor = '#3b82f6'; icone = '▶'; }
  if (evento.tipo === 'tecnico_finalizou') { cor = '#22c55e'; icone = '✅'; }
  if (evento.tipo === 'tecnico_chegou')    { cor = '#3b82f6'; icone = '📍'; }
  if (evento.tipo === 'cancelada')         { cor = '#dc2626'; icone = '❌'; }
  if (evento.tipo === 'reagendada')        { cor = '#f97316'; icone = '🔄'; }
  if (evento.tipo === 'observacao')        { cor = '#6b7280'; icone = '💬'; }
  if (evento.tipo === 'tracking_token_emitido') { cor = '#3b82f6'; icone = '🔗'; }

  return (
    <div style={{ display: 'flex', gap: 10, paddingBottom: 14, position: 'relative' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: 24, height: 24, borderRadius: '50%', background: cor,
          color: 'white', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icone}
        </div>
        <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }}></div>
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
          {TIPO_LABEL[evento.tipo] || evento.tipo}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
          {formatDataHoraSC(evento.ts)}
        </div>
        {evento.observacao && (
          <div style={{ fontSize: 12, marginTop: 4, color: '#374151',
            background: '#f9fafb', padding: '4px 8px', borderRadius: 4 }}>
            {evento.observacao}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Página principal

export default function VisitaDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState(null);
  const [visitaFull, setVisitaFull] = useState(null); // detalhe enriquecido (materiais/relato)
  const [erro, setErro] = useState(null);
  const [acao, setAcao] = useState({});
  const [tecnicoModal, setTecnicoModal] = useState(null);
  const [tecnicoModalVisitas, setTecnicoModalVisitas] = useState([]);

  useEffect(() => { carregar(); }, [id]);

  async function carregar() {
    try {
      const [t, v] = await Promise.all([
        visitas.timeline(id),
        visitas.get(id),
      ]);
      setTimeline(t.data);
      setVisitaFull(v.data);
      setErro(null);
    } catch (e) { setErro(e.uiMessage || 'Erro ao carregar'); }
  }

  async function gerarTrackingLink() {
    try {
      setAcao({ ...acao, gerando: true });
      const r = await visitas.trackingToken(id);
      setAcao({ tracking_url: r.data.tracking_url });
    } catch (e) { setAcao({ erro: e.uiMessage }); }
  }

  async function adicionarObservacao() {
    const texto = prompt('Observação:');
    if (!texto) return;
    try {
      await visitas.addEvento(id, { tipo: 'observacao', observacao: texto });
      carregar();
    } catch (e) { alert(e.uiMessage); }
  }

  async function alterarStatus(novoStatus) {
    if (!confirm(`Alterar status para '${novoStatus}'?`)) return;
    try {
      await visitas.update(id, { status: novoStatus });
      carregar();
    } catch (e) { alert(e.uiMessage); }
  }

  async function abrirOutrasOSDoTecnico() {
    if (!visita.tecnico_id) return;
    try {
      const r = await tecnicosApi.agenda(visita.tecnico_id);
      const dataRef = visita.janela_inicio ? new Date(visita.janela_inicio).toISOString().split('T')[0] : null;
      const doMesmoDia = (r.data || []).filter(v => {
        if (!dataRef || !v.janela_inicio) return true;
        return v.janela_inicio.split('T')[0] === dataRef;
      });
      setTecnicoModalVisitas(doMesmoDia);
      setTecnicoModal({
        id: visita.tecnico_id,
        nome: visita.tecnico_nome,
        apelido: visita.tecnico_apelido,
        avatar_cor: visitaFull?.tecnico_avatar_cor,
      });
    } catch (e) { alert('Erro ao carregar outras OS: ' + (e.uiMessage || e.message)); }
  }

  if (erro) return <div className="card error">{erro}</div>;
  if (!timeline) return <div className="muted">Carregando...</div>;

  const { visita, eventos, paradas, posicoes } = timeline;
  const v = { ...visita, ...(visitaFull || {}) };  // mescla detalhe enriquecido
  const statusVisual = classificarVisita(v);
  const cor = COR_STATUS_VISUAL[statusVisual] || COR_STATUS_VISUAL.agendada;
  const ehTerminal = STATUS_TERMINAIS.has(v.status);
  const concluida = v.status === 'concluida';
  const podeGerarLink = !ehTerminal;

  // Métricas inline
  const atrasoInicio = v.dt_inicio_atendimento && v.janela_inicio
    ? diffMin(v.janela_inicio, v.dt_inicio_atendimento) : null;
  const tempoNoCliente = v.dt_inicio_atendimento && v.dt_fechamento
    ? diffMin(v.dt_inicio_atendimento, v.dt_fechamento) : null;
  const tempoTotal = v.dt_inicio_atendimento && v.dt_fechamento
    ? diffMin(v.janela_inicio, v.dt_fechamento) : null;

  const trajeto = posicoes.map(p => [p.lat, p.lon]);
  const temLocalizacao = v.cliente_lat && v.cliente_lon;
  const centro = temLocalizacao ? [Number(v.cliente_lat), Number(v.cliente_lon)]
                : trajeto[0] || null;

  const mapsUrl = temLocalizacao
    ? `https://www.google.com/maps/dir/?api=1&destination=${v.cliente_lat},${v.cliente_lon}`
    : v.cliente_endereco
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([v.cliente_endereco, v.cliente_bairro, v.cliente_cidade].filter(Boolean).join(', '))}`
      : null;

  return (
    <div className="stack">
      <Link to="/" className="muted" style={{ fontSize: 13 }}>← Voltar</Link>

      {/* ═══ CABEÇALHO ═══ */}
      <div className="card" style={{
        padding: 0, overflow: 'hidden',
        borderLeft: `5px solid ${cor.borda}`,
      }}>
        <div style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: 22 }}>
                  {iconeTipoOs(v.tipo_os)} OS #{osLabel(v)}
                </h1>
                <StatusBadge statusVisual={statusVisual} />
                {v.voalle_protocolo && (
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>interno #i{v.id}</span>
                )}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: '#4b5563', fontWeight: 500 }}>
                {v.tipo_os || '(tipo não informado)'}
              </div>
            </div>
          </div>

          {/* Métricas condicionais */}
          {concluida && (
            <div style={{
              display: 'flex', gap: 32, marginTop: 14, flexWrap: 'wrap',
              padding: '12px 14px', background: '#f9fafb', borderRadius: 8,
            }}>
              <Metrica label="Início no cliente" valor={
                atrasoInicio != null
                  ? (atrasoInicio <= 10 ? `${formatHoraSC(v.dt_inicio_atendimento)} 🟢` : `${formatHoraSC(v.dt_inicio_atendimento)} 🟠`)
                  : '—'
              } cor={atrasoInicio != null && atrasoInicio > 10 ? '#f97316' : '#22c55e'}/>
              <Metrica label="Atraso de início"
                valor={atrasoInicio != null ? `${atrasoInicio >= 0 ? '+' : ''}${Math.round(atrasoInicio)}min` : '—'}
                cor={atrasoInicio != null && atrasoInicio > 10 ? '#dc2626' : '#22c55e'}/>
              <Metrica label="Tempo no cliente" valor={formatDuracaoMin(tempoNoCliente)}/>
              <Metrica label="Tempo total" valor={formatDuracaoMin(tempoTotal)}/>
            </div>
          )}

          {/* Linha de info: janela, técnico, telefone, endereço */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14, marginTop: 14, fontSize: 13,
          }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 2 }}>Cliente</div>
              <div style={{ fontWeight: 700, color: '#111827' }}>{v.cliente_nome || '—'}</div>
              {v.cliente_telefone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 13 }}>
                  <a href={`tel:${v.cliente_telefone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                    📞 {v.cliente_telefone}
                  </a>
                  <button className="btn-icon-sm" title="Copiar telefone"
                          onClick={() => { navigator.clipboard.writeText(v.cliente_telefone); }}>
                    📋
                  </button>
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 2 }}>Janela</div>
              <div style={{ fontWeight: 600 }}>
                {v.janela_inicio ? new Date(v.janela_inicio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
                {' '}
                {formatHoraSC(v.janela_inicio)} → {formatHoraSC(v.janela_fim)}
              </div>
              {v.duracao_estimada_min && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  Duração estimada: {v.duracao_estimada_min}min
                </div>
              )}
            </div>
            {v.tecnico_nome && (
              <div>
                <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 2 }}>Técnico</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AvatarTecnico tecnico={v}/>
                  <div>
                    <div style={{ fontWeight: 600 }}>{v.tecnico_apelido || v.tecnico_nome}</div>
                    {v.tecnico_apelido && v.tecnico_apelido !== v.tecnico_nome && (
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>{v.tecnico_nome}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(v.cliente_endereco || v.cliente_bairro) && (
              <div>
                <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 2 }}>Endereço</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12 }}>
                  <div style={{ flex: 1 }}>
                    {[v.cliente_endereco, v.cliente_bairro, v.cliente_cidade].filter(Boolean).join(', ')}
                  </div>
                  {mapsUrl && (
                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                       className="btn-icon-sm" title="Abrir no Google Maps">
                      🗺️
                    </a>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>
                  <GeocodeBadge qualidade={v.geocode_qualidade}/>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ GRID PRINCIPAL ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, alignItems: 'start' }}>

        {/* Coluna esquerda: registro + mapa */}
        <div className="stack">
          {/* Registro do atendimento (só aparece se tem relato/materiais) */}
          <RegistroAtendimento visita={v}/>

          {/* Mapa (com estado vazio se sem coordenadas) */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {temLocalizacao ? (
              <GoogleMap
                center={centro}
                zoom={15}
                height={380}
                markers={[{
                  lat: Number(v.cliente_lat), lng: Number(v.cliente_lon),
                  label: v.cliente_nome,
                  info: `<strong>${v.cliente_nome || ''}</strong><br/>${v.cliente_endereco || ''}`,
                }]}
                polyline={trajeto}
                circles={paradas.map(p => ({
                  lat: p.lat_centro, lng: p.lon_centro,
                  color: '#16a34a', radius: 9,
                  info: `Parada ${p.tipo}<br/>${p.duracao_min || '?'} min` +
                        (p.distancia_cliente_m != null ? `<br/>${p.distancia_cliente_m.toFixed(0)}m do cliente` : ''),
                }))}
              />
            ) : (
              <MapaVazio visita={v}/>
            )}
          </div>
        </div>

        {/* Coluna direita: ações + timeline */}
        <div className="stack">
          {/* Ações — condicionais por status */}
          <div className="card">
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>⚡ Ações</h3>
            <div className="stack" style={{ gap: 8 }}>
              {podeGerarLink && (
                <>
                  <button className="btn" onClick={gerarTrackingLink} disabled={acao.gerando}>
                    {acao.gerando ? 'Gerando…' : '🔗 Gerar link de acompanhamento'}
                  </button>
                  {acao.tracking_url && (
                    <div style={{ fontSize: 12, wordBreak: 'break-all', background: '#f0fdf4', padding: 8, borderRadius: 6 }}>
                      <a href={acao.tracking_url} target="_blank" rel="noreferrer">{acao.tracking_url}</a>
                      <button className="btn small secondary" style={{ marginTop: 6 }}
                        onClick={() => { navigator.clipboard.writeText(acao.tracking_url); }}>
                        📋 Copiar link
                      </button>
                    </div>
                  )}
                </>
              )}

              <button className="btn secondary" onClick={adicionarObservacao}>
                💬 Adicionar observação
              </button>

              {!ehTerminal && (
                <>
                  <button className="btn secondary" onClick={() => alterarStatus('reagendada')}>
                    🔄 Marcar reagendada
                  </button>
                  <button className="btn secondary" onClick={() => alterarStatus('cancelada')}>
                    ❌ Cancelar visita
                  </button>
                </>
              )}

              {v.tecnico_id && (
                <>
                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '4px 0 0' }}/>
                  <button className="btn secondary" onClick={abrirOutrasOSDoTecnico}>
                    📋 Outras OS do {v.tecnico_apelido || 'técnico'}
                  </button>
                </>
              )}

              {acao.erro && <div className="error">{acao.erro}</div>}
            </div>
          </div>

          {/* Timeline de eventos */}
          <div className="card">
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>
              📅 Timeline · {eventos.length} evento{eventos.length !== 1 ? 's' : ''}
            </h3>
            {eventos.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>
                {v.status === 'agendada'
                  ? 'OS aguardando início. Nada acontece até o técnico marcar chegada.'
                  : 'Sem eventos ainda.'}
              </div>
            ) : (
              <div style={{ paddingTop: 4 }}>
                {eventos.map((e, i) => (
                  <TimelineEvento key={e.id} evento={e} anterior={eventos[i - 1]}/>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de outras OS do técnico (mesmo dia) */}
      {tecnicoModal && (
        <TecnicoVisitasModal
          tecnico={tecnicoModal}
          visitas={tecnicoModalVisitas}
          onClose={() => setTecnicoModal(null)}
        />
      )}
    </div>
  );
}
